// ABOUTME: Client-safe registry of transformation metadata keyed by transformation path.
// ABOUTME: Contains schemas, validation, mock data, and prepopulate config (no context or orchestration).
import { mockData as rusadaToTechlogDefectDeferMockData } from '../integrations/rusada-techlog/defect/mocks';
import { rusadaDefectDeferToTechlogSchema } from '../integrations/rusada-techlog/defect/schema';
import {
  ZRusadaToTechlogDefectDeferInput,
  ZRusadaToTechlogDefectDeferOutput,
} from '../integrations/rusada-techlog/defect/validation';
import { mockData as rusadaToTechlogFlightMockData } from '../integrations/rusada-techlog/flight/mocks';
import rusadaFlightToTechlogSchema from '../integrations/rusada-techlog/flight/schema';
import {
  ZRusadaToTechLogFlightInput,
  ZRusadaToTechLogFlightOutput,
} from '../integrations/rusada-techlog/flight/validation';
import { mockData as rusadaScheduledMxItemMockData } from '../integrations/rusada-techlog/scheduled-mx-item/mocks';
import rusadaScheduledMxItemToTechlogSchema from '../integrations/rusada-techlog/scheduled-mx-item/schema';
import {
  ZRusadaToTechlogScheduledMxItemInput,
  ZRusadaToTechlogScheduledMxItemOutput,
} from '../integrations/rusada-techlog/scheduled-mx-item/validation';
import { mockData as skylegsToTechlogFlightMockData } from '../integrations/skylegs-techlog/flight/mocks';
import { skylegsFlightToTechlogSchema } from '../integrations/skylegs-techlog/flight/schema';
import {
  ZSkylegsToTechlogFlightInput,
  ZSkylegsToTechlogFlightOutput,
} from '../integrations/skylegs-techlog/flight/validation';
import { mockData as techlogToRaasFlightMockData } from '../integrations/techlog-raas/flight/mocks';
import { techlogFlightToRaasSchema } from '../integrations/techlog-raas/flight/schema';
import {
  ZTechlogToRaasFlightInput,
  ZTechlogToRaasFlightOutput,
} from '../integrations/techlog-raas/flight/validation';
import {
  mockData as techlogToRusadaDefectMockData,
  mockDataResolved as techlogToRusadaDefectCloseMockData,
  mockDataDeferred as techlogToRusadaDefectDeferMockData,
} from '../integrations/techlog-rusada/defect/mocks';
import {
  techlogDefectToRusadaSchema,
  techlogDefectCloseToRusadaSchema,
  techlogDefectDeferToRusadaSchema,
} from '../integrations/techlog-rusada/defect/schema';
import {
  ZTechLogToRusadaDefectInput,
  ZTechLogToRusadaDefectCloseInput,
  ZTechLogToRusadaDefectDeferInput,
  ZTechLogToRusadaDefectLinkToSectorInput,
  ZTechLogToRusadaDefectOutput,
  ZTechLogToRusadaDefectCloseOutput,
  ZTechLogToRusadaDefectDeferOutput,
  ZTechLogToRusadaDefectLinkToSectorOutput,
} from '../integrations/techlog-rusada/defect/validation';
import { mockData as techlogToRusadaFlightMockData } from '../integrations/techlog-rusada/flight/mocks';
import { techlogFlightToRusadaSchema } from '../integrations/techlog-rusada/flight/schema';
import {
  ZTechLogToRusadaFlightInput,
  ZTechLogToRusadaFlightOutput,
} from '../integrations/techlog-rusada/flight/validation';
import { mockData as techlogToRusadaOilAndFuelMockData } from '../integrations/techlog-rusada/oil-and-fuel/mocks';
import { techlogOilAndFuelToRusadaSchema } from '../integrations/techlog-rusada/oil-and-fuel/schema';
import {
  ZTechLogToRusadaOilAndFuelInput,
  ZTechLogToRusadaOilAndFuelOutput,
} from '../integrations/techlog-rusada/oil-and-fuel/validation';
import { mockData as techlogToRusadaOilUpliftMockData } from '../integrations/techlog-rusada/oil-uplift/mocks';
import { techlogOilUpliftToRusadaSchema } from '../integrations/techlog-rusada/oil-uplift/schema';
import {
  ZTechLogToRusadaOilUpliftInput,
  ZTechLogToRusadaOilUpliftOutput,
} from '../integrations/techlog-rusada/oil-uplift/validation';
import { mockData as techlogToRusadaTripUpdateMockData } from '../integrations/techlog-rusada/tripUpdates/mocks';
import { techlogTripUpdateToRusadaSchema } from '../integrations/techlog-rusada/tripUpdates/schema';
import {
  ZTechLogToRusadaTripUpdateInput,
  ZTechLogToRusadaTripUpdateOutput,
} from '../integrations/techlog-rusada/tripUpdates/validation';
import { mockData as techlogToSkylegsFlightMockData } from '../integrations/techlog-skylegs/flight/mocks';
import { techlogFlightToSkylegsSchema } from '../integrations/techlog-skylegs/flight/schema';
import {
  ZTechlogToSkylegsFlightInput,
  ZTechlogToSkylegsFlightOutput,
} from '../integrations/techlog-skylegs/flight/validation';
import {
  ActionType,
  createTransformationPathKey,
  Destination,
  EntityType,
  Source,
} from './enums';
import type { ContextType, MappingSchema, PrepopulateConfig } from './types';
import { z } from 'zod';

export interface TransformationMetadataConfig {
  inputZod: z.ZodType;
  outputZod: z.ZodType;
  schema: MappingSchema;
  mockData: ContextType;
  prepopulateConfig?: PrepopulateConfig;
}

export const TRANSFORMATION_METADATA_REGISTRY: Record<
  string,
  TransformationMetadataConfig
> = {
  [createTransformationPathKey(
    Source.RUSADA,
    Destination.TECHLOG,
    EntityType.FLIGHT,
  )]: {
    inputZod: ZRusadaToTechLogFlightInput,
    outputZod: ZRusadaToTechLogFlightOutput,
    schema: rusadaFlightToTechlogSchema as MappingSchema,
    mockData: rusadaToTechlogFlightMockData,
  },

  [createTransformationPathKey(
    Source.RUSADA,
    Destination.TECHLOG,
    EntityType.MXITEM,
  )]: {
    inputZod: ZRusadaToTechlogScheduledMxItemInput,
    outputZod: ZRusadaToTechlogScheduledMxItemOutput,
    schema: rusadaScheduledMxItemToTechlogSchema as MappingSchema,
    mockData: rusadaScheduledMxItemMockData,
  },

  // DEFER is the only action: defects originate in TechLog, only deferrals flow back
  [createTransformationPathKey(
    Source.RUSADA,
    Destination.TECHLOG,
    EntityType.DEFECT,
  )]: {
    inputZod: ZRusadaToTechlogDefectDeferInput,
    outputZod: ZRusadaToTechlogDefectDeferOutput,
    schema: rusadaDefectDeferToTechlogSchema as MappingSchema,
    mockData: rusadaToTechlogDefectDeferMockData,
  },

  [createTransformationPathKey(
    Source.SKYLEGS,
    Destination.TECHLOG,
    EntityType.FLIGHT,
  )]: {
    inputZod: ZSkylegsToTechlogFlightInput,
    outputZod: ZSkylegsToTechlogFlightOutput,
    schema: skylegsFlightToTechlogSchema as MappingSchema,
    mockData: skylegsToTechlogFlightMockData,
  },

  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.DEFECT,
  )]: {
    inputZod: ZTechLogToRusadaDefectInput,
    outputZod: ZTechLogToRusadaDefectOutput,
    schema: techlogDefectToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaDefectMockData,
  },

  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RAAS,
    EntityType.FLIGHT,
  )]: {
    inputZod: ZTechlogToRaasFlightInput,
    outputZod: ZTechlogToRaasFlightOutput,
    schema: techlogFlightToRaasSchema as MappingSchema,
    mockData: techlogToRaasFlightMockData,
  },

  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.SKYLEGS,
    EntityType.FLIGHT,
  )]: {
    inputZod: ZTechlogToSkylegsFlightInput,
    outputZod: ZTechlogToSkylegsFlightOutput,
    schema: techlogFlightToSkylegsSchema as MappingSchema,
    mockData: techlogToSkylegsFlightMockData,
  },

  // Action-specific: TECHLOG.RUSADA.DEFECT.CLOSE for closing defects
  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.DEFECT,
    ActionType.CLOSE,
  )]: {
    inputZod: ZTechLogToRusadaDefectCloseInput,
    outputZod: ZTechLogToRusadaDefectCloseOutput,
    schema: techlogDefectCloseToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaDefectCloseMockData,
  },

  // Action-specific: TECHLOG.RUSADA.DEFECT.DEFER for deferring defects
  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.DEFECT,
    ActionType.DEFER,
  )]: {
    inputZod: ZTechLogToRusadaDefectDeferInput,
    outputZod: ZTechLogToRusadaDefectDeferOutput,
    schema: techlogDefectDeferToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaDefectDeferMockData,
  },

  // Action-specific: TECHLOG.RUSADA.DEFECT.LINK_TO_SECTOR for linking defects to their TechLog sector
  // Reuses the CREATE/UPDATE schema — full defect PUT with guaranteed sector fields
  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.DEFECT,
    ActionType.LINK_TO_SECTOR,
  )]: {
    inputZod: ZTechLogToRusadaDefectLinkToSectorInput,
    outputZod: ZTechLogToRusadaDefectLinkToSectorOutput,
    schema: techlogDefectToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaDefectMockData,
  },

  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.FLIGHT,
  )]: {
    inputZod: ZTechLogToRusadaFlightInput,
    outputZod: ZTechLogToRusadaFlightOutput,
    schema: techlogFlightToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaFlightMockData,
  },

  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.TRIP_UPDATE,
  )]: {
    inputZod: ZTechLogToRusadaTripUpdateInput,
    outputZod: ZTechLogToRusadaTripUpdateOutput,
    schema: techlogTripUpdateToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaTripUpdateMockData,
  },

  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.OIL_AND_FUEL,
  )]: {
    inputZod: ZTechLogToRusadaOilAndFuelInput,
    outputZod: ZTechLogToRusadaOilAndFuelOutput,
    schema: techlogOilAndFuelToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaOilAndFuelMockData,
  },
  [createTransformationPathKey(
    Source.TECHLOG,
    Destination.RUSADA,
    EntityType.OIL_UPLIFT,
  )]: {
    inputZod: ZTechLogToRusadaOilUpliftInput,
    outputZod: ZTechLogToRusadaOilUpliftOutput,
    schema: techlogOilUpliftToRusadaSchema as MappingSchema,
    mockData: techlogToRusadaOilUpliftMockData,
  },
};

export function getTransformationMetadataForTransformationPath(input: {
  source: Source;
  destination: Destination;
  entity: EntityType;
  action?: ActionType;
}): TransformationMetadataConfig {
  // Try action-specific key first, then fall back to base key
  if (input.action) {
    const actionKey = createTransformationPathKey(
      input.source,
      input.destination,
      input.entity,
      input.action,
    );
    const actionConfig = TRANSFORMATION_METADATA_REGISTRY[actionKey];
    if (actionConfig) {
      return actionConfig;
    }
  }

  const baseKey = createTransformationPathKey(
    input.source,
    input.destination,
    input.entity,
  );
  const config = TRANSFORMATION_METADATA_REGISTRY[baseKey];
  if (!config) {
    throw new Error(`No transformation metadata found for ${baseKey}`);
  }
  return config;
}

export function getAvailableTransformationMetadataKeys(): string[] {
  return Object.keys(TRANSFORMATION_METADATA_REGISTRY);
}

export function clientTransformationPathConfig(
  source: Source,
  destination: Destination,
  entity: EntityType,
  action?: ActionType,
): TransformationMetadataConfig | undefined {
  try {
    return getTransformationMetadataForTransformationPath({
      source,
      destination,
      entity,
      action,
    });
  } catch {
    return undefined;
  }
}

export const getClientTransformationConfig = clientTransformationPathConfig;
